import { useEffect, useMemo, useState } from 'react';

const getVisiblePages = (currentPage, totalPages, maxVisiblePages) => {
  if (!totalPages) {
    return [];
  }

  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const halfWindow = Math.floor(maxVisiblePages / 2);
  let startPage = Math.max(1, currentPage - halfWindow);
  let endPage = startPage + maxVisiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = endPage - maxVisiblePages + 1;
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
};

function useMercurxPagination(
  sourceData = [],
  itemsPerPage = 10,
  maxVisiblePages = 5,
  options = {}
) {
  const { resetPageOnDataChange = true } = options;
  const normalizedData = useMemo(
    () => (Array.isArray(sourceData) ? sourceData : []),
    [sourceData]
  );
  const totalPages = Math.ceil(normalizedData.length / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(totalPages ? 1 : 0);

  useEffect(() => {
    setCurrentPage((previousPage) => {
      if (!totalPages) {
        return 0;
      }

      if (resetPageOnDataChange || previousPage <= 0) {
        return 1;
      }

      return Math.min(previousPage, totalPages);
    });
  }, [resetPageOnDataChange, sourceData, totalPages]);

  const pagedData = useMemo(() => {
    if (!currentPage || !totalPages) {
      return [];
    }

    const firstIndex = (currentPage - 1) * itemsPerPage;

    return normalizedData.slice(firstIndex, firstIndex + itemsPerPage);
  }, [currentPage, itemsPerPage, normalizedData, totalPages]);

  const visiblePages = useMemo(
    () => getVisiblePages(currentPage || 1, totalPages, maxVisiblePages),
    [currentPage, maxVisiblePages, totalPages]
  );

  const goToPage = (pageNumber) => {
    if (!totalPages) {
      return;
    }

    const boundedPage = Math.min(Math.max(pageNumber, 1), totalPages);
    setCurrentPage(boundedPage);
  };

  return {
    currentPage,
    totalPages,
    visiblePages,
    pagedData,
    rowOffset: currentPage > 0 ? (currentPage - 1) * itemsPerPage : 0,
    isFirstPage: currentPage <= 1,
    isLastPage: !totalPages || currentPage >= totalPages,
    goToPage,
    goToFirstPage: () => goToPage(1),
    goToPrevPage: () => goToPage(currentPage - 1),
    goToNextPage: () => goToPage(currentPage + 1),
    goToLastPage: () => goToPage(totalPages),
  };
}

export default useMercurxPagination;
